import { PlusOutlined } from '@ant-design/icons';
import { getLeadAttributes, type GetLeadAttributesType } from '@repo/advanced-filters';
import {
  filterMailMergeOptions, getActivityAttributes, type GetActivityAttributesProps, getMailMergeValue,
} from '@repo/crm-atoms';
import { useGetGraphqlDataQuery, type UseQueryResult } from '@repo/services';
import { message } from '@repo/ui';
import { translate, TranslationEntity } from '@repo/utils';
import { type NodeProps } from '@xyflow/react';
import {
  AutoComplete, Button, Card, Flex, Input, List,
} from 'antd';
import { useAutomationDispatch } from 'automations/hooks/useAutomationDispatch';
import useCommonNodeHandles from 'automations/hooks/useCommonNodeHandles';
import useMailMergeFields from 'automations/hooks/useMailMergeFields';
import {
  getParentIdSelector, isDealRelatedTriggerNodeSelector, triggeredActivityForMailMergeSelector,
} from 'automations/selectors/reactFlow';
import { addAdditionalOptions } from 'automations/store/reactFlowSlice';
import {
  type SendWebengageEventNodeData, type SendWebengageEventNodeType,
} from 'automations/types/nodeSchemas/actionNodes';
import { nodeSchemas } from 'automations/types/nodeTypes';
import { saveActionNode } from 'automations/utils/actionNode';
import {
  useCallback, useMemo, useState,
} from 'react';
import { useSelector } from 'react-redux';
import { ZodError } from 'zod';

import MappingRow from './MappingRow';
import styles from './SendWebengageEventContent.module.scss';

type SendWebengageEventConfig = SendWebengageEventNodeData['additionalOptions']['sendWebengageEvent'];
type Mapping = SendWebengageEventConfig['mappings'][number];

const initialMapping: Partial<Mapping> = {
  webengageAttribute: '',
  sourceAttribute: '',
};

export default function SendWebengageEventForm({
  node,
}: {
  node: NodeProps<SendWebengageEventNodeType>;
}) {
  const dispatch = useAutomationDispatch();
  const { setMenuOpen, contentMode } = useCommonNodeHandles(node);
  const parentNodeId = useSelector(getParentIdSelector(node.id));
  const triggeredActivityId = useSelector(triggeredActivityForMailMergeSelector);
  const isDealTrigger = useSelector(isDealRelatedTriggerNodeSelector);

  const [isLoading, setIsLoading] = useState(false);
  const [eventName, setEventName] = useState<string>(
    node.data?.additionalOptions?.sendWebengageEvent?.eventName ?? '',
  );
  const [mappings, setMappings] = useState<Partial<Mapping>[]>(
    node.data?.additionalOptions?.sendWebengageEvent?.mappings ?? [initialMapping],
  );

  const { options: mailMergeOptions } = useMailMergeFields({});

  const {
    data: leadAttributesData,
    isFetching: leadAttributesLoading,
  } = useGetGraphqlDataQuery<UseQueryResult<GetLeadAttributesType>>({ document: getLeadAttributes });

  // There is no query for activity attributes across all activity types, and the saved mapping
  // carries no activity id (the backend resolves lead_activity_attribute against whatever activity
  // is attached to the entity at runtime), so the options are scoped to the trigger's own activity.
  const {
    data: activityAttributesData,
    isFetching: activityAttributesLoading,
  } = useGetGraphqlDataQuery<UseQueryResult<GetActivityAttributesProps>>({
    document: getActivityAttributes,
    variables: { activityId: triggeredActivityId! },
  }, { skip: !triggeredActivityId });

  const leadAttributeOptions = useMemo(() => leadAttributesData?.leadAttributes.map(
    ({ label, schemaName }) => ({ label, value: schemaName }),
  ) ?? [], [leadAttributesData?.leadAttributes]);

  const activityAttributeOptions = useMemo(() => activityAttributesData?.activityAttribute.map(
    ({ attributeName, schemaName }) => ({ label: attributeName, value: schemaName }),
  ) ?? [], [activityAttributesData?.activityAttribute]);

  const showMailMergeOptions = eventName.includes('@');
  const mailMergeSearchString = eventName.slice(eventName.lastIndexOf('@') + 1);
  const filteredMailMergeOptions = filterMailMergeOptions(mailMergeOptions, mailMergeSearchString);

  // AutoComplete fires onChange with the raw option value before onSelect, so a functional
  // updater would see that value as `prev` instead of what the user typed.
  const handleEventNameSelect = useCallback((newValue: string) => {
    setEventName(eventName.slice(0, eventName.lastIndexOf('@')) + getMailMergeValue(newValue));
  }, [eventName]);

  const handleAddMapping = useCallback(() => {
    setMappings((prev) => [...prev, initialMapping]);
  }, []);

  const handleRemoveMapping = useCallback((index: number) => {
    setMappings((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleMappingChange = useCallback((index: number, patch: Partial<Mapping>) => {
    setMappings((prev) => prev.map((mapping, i) => (i === index ? { ...mapping, ...patch } : mapping)));
  }, []);

  const handleSave = useCallback(async () => {
    setIsLoading(true);

    try {
      const parsedNodeData = nodeSchemas.SendWebengageEventActionNodeData.parse({
        ...node.data,
        additionalOptions: {
          sendWebengageEvent: { eventName, mappings },
        },
      });

      dispatch(addAdditionalOptions({
        nodeId: node.id,
        additionalOptions: parsedNodeData.additionalOptions,
      }));

      await dispatch(saveActionNode(node.id, parentNodeId, contentMode));
      setMenuOpen(false);
    } catch (error: any) {
      const errorMessage = error instanceof ZodError
        ? error.issues.map((issue) => issue.message).join(', ')
        : error?.data?.message ?? translate(
          `Failed to update #{${TranslationEntity.automation}}`,
          [{ capitalize: true }],
        );
      message.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [contentMode, dispatch, eventName, mappings, node.data, node.id, parentNodeId, setMenuOpen]);

  return (
    <>
      <Card title="Event">
        <AutoComplete
          options={showMailMergeOptions ? filteredMailMergeOptions : []}
          value={eventName}
          onChange={setEventName}
          onSelect={handleEventNameSelect}
          className={styles.eventNameInput}
        >
          <Input placeholder="e.g. Payment Successful, or type @ for dynamic fields" />
        </AutoComplete>
      </Card>

      <Card title="Attribute Mappings" className={styles.mappingsCard}>
        <List
          footer={(
            <Button icon={<PlusOutlined />} type="link" onClick={handleAddMapping} className={styles.addButton}>
              Add Mapping
            </Button>
          )}
          dataSource={mappings}
          renderItem={(mapping, index) => (
            <MappingRow
              mapping={mapping}
              onChange={(patch) => handleMappingChange(index, patch)}
              onRemove={index > 0 ? () => handleRemoveMapping(index) : undefined}
              leadAttributeOptions={leadAttributeOptions}
              leadAttributeOptionsLoading={leadAttributesLoading}
              activityAttributeOptions={activityAttributeOptions}
              activityAttributeOptionsLoading={activityAttributesLoading}
              activityAttributesAvailable={Boolean(triggeredActivityId)}
              dealAttributesAvailable={isDealTrigger}
            />
          )}
        />
      </Card>

      <Flex justify="flex-end" className={styles.footer}>
        <Button type="primary" loading={isLoading} onClick={handleSave}>
          Save Action
        </Button>
      </Flex>
    </>
  );
}
